import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { trackDemoEvent } from './demoTracking';

/**
 * Headless demo analytics listener (mounted inside the Router). Fires "Demo Page Viewed" on every
 * route change — one location listener covers all nav since sidebar items are history.push — and
 * "Demo Session Ended" with foreground dwell time on the way out. Tile/task events live elsewhere.
 */

// Map a pathname to a coarse product area to keep "Demo Page Viewed" low-cardinality.
const SECTION_BY_SEGMENT: Record<string, string> = {
    dashboard: 'home',
    tasks: 'tasks',
    documents: 'documents',
    tax: 'taxes',
    bookkeeping: 'accounting',
    payroll: 'payroll',
    insurance: 'insurance',
    referrals: 'referrals',
    settings: 'settings',
};

function sectionFromPath(pathname: string): string {
    const segment = pathname.split('/').filter(Boolean)[0] ?? 'home';
    return SECTION_BY_SEGMENT[segment] ?? segment;
}

export default function DemoAnalytics() {
    const { pathname } = useLocation();

    // One page-view per route change (and on first load).
    useEffect(() => {
        trackDemoEvent('Demo Page Viewed', { section: sectionFromPath(pathname), path: pathname });
    }, [pathname]);

    // Session length — accumulate only foreground time, flush once when the tab goes away.
    useEffect(() => {
        let activeSince = Date.now();
        let engagedMs = 0;
        let ended = false;

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                engagedMs += Date.now() - activeSince;
            } else {
                activeSince = Date.now();
            }
        };

        const flush = () => {
            if (ended) {
                return;
            }
            ended = true;
            if (document.visibilityState === 'visible') {
                engagedMs += Date.now() - activeSince;
            }
            trackDemoEvent('Demo Session Ended', { engaged_seconds: Math.round(engagedMs / 1000) }, { beacon: true });
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', flush);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', flush);
        };
    }, []);

    return null;
}
